import { useState, useEffect, useCallback } from "react";
import { Loader2, RefreshCw, TrendingUp, Users, FileDown, AlertTriangle } from "lucide-react";
import { apiFetch } from "@/portal/api";
import { fmtCZK } from "@/flotila/fleetUtils";
import { toast } from "sonner";

const VAT = 0.21;
const monthNow = () => new Date().toISOString().slice(0, 7);

const Stat = ({ label, value, sub, tone = "text-white", testid }) => (
  <div className="glass rounded-2xl p-4" data-testid={testid}>
    <p className="text-xs text-zinc-400 mb-1">{label}</p>
    <p className={`text-xl sm:text-2xl font-bold font-['Cabinet_Grotesk'] ${tone}`}>{fmtCZK(value)}</p>
    {sub != null && <p className="text-[11px] text-zinc-500 mt-0.5">{sub}</p>}
  </div>
);

const ProfitabilityTab = ({ token }) => {
  const now = new Date();
  const [from, setFrom] = useState(`${now.getFullYear()}-01`);
  const [to, setTo] = useState(monthNow());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [onlyLoss, setOnlyLoss] = useState(false);
  const [basis, setBasis] = useState("gross"); // gross = s DPH, net = bez DPH

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await apiFetch(`/admin/profitability?from=${from}&to=${to}`, { token }));
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }, [from, to, token]);
  useEffect(() => { load(); }, [load]);

  const importContracts = async () => {
    setImporting(true);
    try {
      const r = await apiFetch(`/admin/cars/import-from-contracts`, { method: "POST", token });
      toast.success(`Hotovo: ${r.created} nových vozů, ${r.updated} doplněno ze smluv.`);
      load();
    } catch (e) { toast.error(e.message); } finally { setImporting(false); }
  };

  // s DPH (gross) is what the user enters; bez DPH (net) = gross / 1.21
  const show = (v) => (basis === "gross" ? v : v / (1 + VAT));
  const other = (v) => (basis === "gross" ? v / (1 + VAT) : v);
  const otherLabel = basis === "gross" ? "bez DPH" : "s DPH";
  const sub = (v) => `${fmtCZK(other(v))} ${otherLabel}`;

  const t = data?.totals;
  const lossCount = data ? data.cars.filter((c) => c.profit < 0).length : 0;
  const visibleCars = data ? (onlyLoss ? data.cars.filter((c) => c.profit < 0) : data.cars) : [];

  return (
    <div className="space-y-6" data-testid="profitability-tab">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-['Cabinet_Grotesk'] text-2xl font-bold inline-flex items-center gap-2"><TrendingUp className="w-6 h-6 text-orange-400" /> Ziskovost & vyúčtování</h2>
          <p className="text-sm text-zinc-400 mt-1">Výnosy, náklady, výplaty partnerům a náš čistý zisk za zvolené období.</p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div className="inline-flex rounded-full border border-white/15 p-1 text-sm" data-testid="profit-vat-toggle">
            <button onClick={() => setBasis("gross")} className={`px-3 py-1 rounded-full transition-colors ${basis === "gross" ? "bg-orange-500 text-black font-semibold" : "text-zinc-400 hover:text-white"}`} data-testid="profit-vat-gross">s DPH</button>
            <button onClick={() => setBasis("net")} className={`px-3 py-1 rounded-full transition-colors ${basis === "net" ? "bg-orange-500 text-black font-semibold" : "text-zinc-400 hover:text-white"}`} data-testid="profit-vat-net">bez DPH</button>
          </div>
          <label className="text-xs text-zinc-400">Od<input type="month" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className="input-dark block mt-1" data-testid="profit-from" /></label>
          <label className="text-xs text-zinc-400">Do<input type="month" value={to} min={from} max={monthNow()} onChange={(e) => setTo(e.target.value)} className="input-dark block mt-1" data-testid="profit-to" /></label>
          <button onClick={load} className="p-2 rounded-full border border-white/15 text-zinc-300 hover:text-white" title="Obnovit" data-testid="profit-refresh"><RefreshCw className="w-4 h-4" /></button>
          <button onClick={importContracts} disabled={importing} className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-full border border-white/15 text-zinc-300 hover:text-white disabled:opacity-60" title="Projde smlouvy a doplní chybějící vozy / SPZ do flotily" data-testid="profit-import-contracts">{importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} Doplnit vozy ze smluv</button>
          {lossCount > 0 && (
            <button onClick={() => setOnlyLoss((v) => !v)} className={`inline-flex items-center gap-2 text-sm px-3 py-2 rounded-full border transition-colors ${onlyLoss ? "bg-red-500/20 border-red-500/50 text-red-300" : "border-red-500/30 text-red-400 hover:bg-red-500/10"}`} title="Zobrazit jen vozy ve ztrátě" data-testid="profit-loss-filter"><AlertTriangle className="w-4 h-4" /> Ve ztrátě: {lossCount}</button>
          )}
        </div>
      </div>

      {loading && !data ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-orange-400" /></div>
      ) : !data ? null : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="profit-totals">
            <Stat label="Výnos (zaplaceno)" value={show(t.revenue_paid)} sub={sub(t.revenue_paid)} tone="text-emerald-400" testid="profit-total-revenue" />
            <Stat label="Plánováno (dle smluv)" value={show(t.revenue_planned)} sub={sub(t.revenue_planned)} tone="text-zinc-300" testid="profit-total-planned" />
            <Stat label="Náklady" value={show(t.expenses)} sub={sub(t.expenses)} tone="text-amber-400" testid="profit-total-expenses" />
            <Stat label="Výplaty partnerům" value={show(t.partner_payout)} sub={sub(t.partner_payout)} tone="text-sky-400" testid="profit-total-payout" />
            <Stat label="Čistý zisk EffectDeal" value={show(t.profit)} sub={sub(t.profit)} tone={t.profit >= 0 ? "text-emerald-400" : "text-red-400"} testid="profit-total-profit" />
          </div>
          <p className="text-xs text-zinc-500 -mt-2">Období {data.from} – {data.to} · {data.months} měs. · zobrazeno <span className="text-zinc-300">{basis === "gross" ? "s DPH" : "bez DPH"}</span> (DPH 21 %) · výplata partnerovi = pevná částka/měs × počet měsíců.</p>

          {data.partners.length > 0 && (
            <div className="glass rounded-2xl overflow-hidden" data-testid="profit-partners">
              <div className="px-4 py-3 border-b border-white/5 inline-flex items-center gap-2 font-semibold"><Users className="w-4 h-4 text-sky-400" /> Vyúčtování partnerům</div>
              <div className="overflow-x-auto">
                <table className="min-w-[420px] w-full text-sm">
                  <thead className="bg-white/5 text-zinc-400 text-xs uppercase tracking-wider">
                    <tr><th className="px-4 py-2 text-left">Partner</th><th className="px-4 py-2 text-right">Vozů</th><th className="px-4 py-2 text-right">K výplatě ({basis === "gross" ? "s DPH" : "bez DPH"})</th></tr>
                  </thead>
                  <tbody>
                    {data.partners.map((p) => (
                      <tr key={p.partner_id} className="border-t border-white/5" data-testid={`profit-partner-${p.partner_id}`}>
                        <td className="px-4 py-2.5 text-white">{p.partner_name || "—"}</td>
                        <td className="px-4 py-2.5 text-right text-zinc-300">{p.cars}</td>
                        <td className="px-4 py-2.5 text-right font-semibold text-sky-300">{fmtCZK(show(p.payout))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="glass rounded-2xl overflow-hidden" data-testid="profit-cars">
            <div className="px-4 py-3 border-b border-white/5 font-semibold">Podle vozu <span className="text-xs font-normal text-zinc-500">({basis === "gross" ? "s DPH" : "bez DPH"})</span></div>
            <div className="overflow-x-auto">
              <table className="min-w-[760px] w-full text-sm">
                <thead className="bg-white/5 text-zinc-400 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-2 text-left">Vůz</th>
                    <th className="px-4 py-2 text-left">Partner</th>
                    <th className="px-4 py-2 text-right">Výnos (zapl.)</th>
                    <th className="px-4 py-2 text-right">Plánováno</th>
                    <th className="px-4 py-2 text-right">Náklady</th>
                    <th className="px-4 py-2 text-right">Výplata partn.</th>
                    <th className="px-4 py-2 text-right">Náš zisk</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleCars.length === 0 ? (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-zinc-500">{onlyLoss ? "Žádný vůz ve ztrátě." : "Žádná data za období."}</td></tr>
                  ) : visibleCars.map((c) => {
                    const loss = c.profit < 0;
                    return (
                      <tr key={c.car_id} className={`border-t border-white/5 ${loss ? "bg-red-500/5" : ""}`} data-testid={`profit-car-${c.car_id}`}>
                        <td className={`px-4 py-2.5 ${loss ? "border-l-2 border-l-red-500" : ""}`}><span className="text-white">{c.label}</span>{loss && <span className="ml-2 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-300 align-middle" data-testid={`profit-loss-badge-${c.car_id}`}><AlertTriangle className="w-3 h-3" /> Ztráta</span>}{c.license_plate && <span className="block text-[11px] font-mono text-zinc-500">{c.license_plate}</span>}</td>
                        <td className="px-4 py-2.5 text-zinc-400 text-xs">{c.partner_name || "EffectDeal"}</td>
                        <td className="px-4 py-2.5 text-right text-emerald-400">{fmtCZK(show(c.revenue_paid))}</td>
                        <td className="px-4 py-2.5 text-right text-zinc-400">{fmtCZK(show(c.revenue_planned))}</td>
                        <td className="px-4 py-2.5 text-right text-amber-400">{fmtCZK(show(c.expenses))}</td>
                        <td className="px-4 py-2.5 text-right text-sky-300">{c.partner_payout ? fmtCZK(show(c.partner_payout)) : "—"}</td>
                        <td className={`px-4 py-2.5 text-right font-semibold ${c.profit >= 0 ? "text-emerald-400" : "text-red-400"}`}>{fmtCZK(show(c.profit))}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProfitabilityTab;
